// Auth Context - httpOnly Cookie-based Authentication
// Tokens are stored in httpOnly cookies, completely inaccessible to JavaScript
// This provides 100% XSS protection

import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Configure axios to send cookies with cross-origin requests
// This is required for httpOnly cookie auth
axios.defaults.withCredentials = true;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // Auth is handled entirely via httpOnly cookies
      // Just verify if we have a valid session by calling /me
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch {
        // Not authenticated or session expired
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    // Token is set via httpOnly cookie by the server automatically
    setUser(userData);
    setIsNewUser(false);
    return userData;
  };

  const register = async (email, password, firmName, fullName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      firm_name: firmName,
      full_name: fullName
    });
    const { user: userData } = response.data;
    // Token is set via httpOnly cookie by the server automatically
    setUser(userData);
    setIsNewUser(true);
    return userData;
  };

  const completeSetup = async () => {
    try {
      await axios.post(`${API}/auth/complete-setup`);
      setUser(prev => ({ ...prev, setup_completed: true }));
      setIsNewUser(false);
    } catch {
      // Silent fail - setup completion is non-critical
    }
  };

  const logout = async () => {
    // Clear server-side cookies via logout endpoint
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Silent fail - logout is best-effort
    }
    setUser(null);
    setIsNewUser(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isNewUser,
      login,
      register,
      completeSetup,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
